import { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { PALETTE as P } from "../tokens";
import { useAuth } from "../context/AuthContext";
import { fitbitApi } from "../api/fitbit";
import { ouraApi } from "../api/oura";
import { isOuraUIEnabled } from "../utils/features";
import { wearablesApi } from "../api/wearables";
import {
  connectHealthKit,
  healthKitLastSyncedAt,
  isHealthKitConnected,
  isHealthKitPending,
  isHealthKitSupported,
  syncHealthKitToday,
} from "../services/healthkit";
import { isIOSBrowser, isNativeIOS } from "../utils/platform";

// Local token — sage green that harmonizes with the vellum + antique
// gold base. Used only for the "Connected" status pill and dot.
// Intentionally muted (no pure #2ecc71-style brightness) so the screen
// reads as quietly "healthy" rather than like a web dashboard.
const HEALTH_GREEN = "#7AA078";
const HEALTH_GREEN_SOFT = "rgba(122, 160, 120, 0.12)";

const STYLES = `
  * { box-sizing: border-box; margin: 0; padding: 0; }

  .wearables-screen {
    font-family: 'DM Sans', sans-serif;
    background: ${P.bg};
    min-height: 100vh;
    color: ${P.text};
    max-width: 420px;
    margin: 0 auto;
    padding: 0 20px 48px;
  }

  @keyframes fadeUp {
    from { opacity: 0; transform: translateY(12px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  .fu { animation: fadeUp 0.42s ease forwards; opacity: 0; }
  .d1 { animation-delay: 0.05s; }
  .d2 { animation-delay: 0.15s; }
  .d3 { animation-delay: 0.25s; }

  .topbar {
    display: flex; align-items: center; gap: 10px;
    /* Clear the Dynamic Island / status-bar safe area so the back
       control is tappable on notched devices (was a flat 18px, which
       collided with the inset). */
    padding: calc(env(safe-area-inset-top) + 18px) 0 8px;
  }
  .topbar button {
    background: none; border: none; cursor: pointer;
    padding: 6px; margin-left: -6px;
    color: ${P.textMid};
    display: flex; align-items: center;
  }
  .topbar button:hover { color: ${P.primary}; }

  .title {
    font-family: 'Playfair Display', Georgia, serif;
    font-size: 26px;
    font-style: italic;
    color: ${P.text};
    margin: 6px 0 4px;
    line-height: 1.15;
  }
  .subtitle {
    font-size: 13px;
    color: ${P.textMid};
    font-weight: 300;
    line-height: 1.55;
    margin-bottom: 22px;
  }

  .integration-card {
    background: linear-gradient(
      135deg,
      rgba(158,132,86,0.07) 0%,
      rgba(158,132,86,0.02) 100%
    );
    border: 1px solid ${P.border};
    border-radius: 20px;
    padding: 22px;
    box-shadow: 0 1px 16px rgba(28,24,20,0.05);
    margin-bottom: 12px;
  }

  .integration-header {
    display: flex;
    align-items: center;
    gap: 14px;
    margin-bottom: 14px;
  }
  .integration-icon {
    width: 44px; height: 44px;
    border-radius: 12px;
    background: ${P.primarySoft};
    flex-shrink: 0;
    display: flex; align-items: center; justify-content: center;
  }
  .integration-title {
    font-size: 16px;
    color: ${P.text};
    font-weight: 500;
    margin-bottom: 3px;
  }
  .integration-sub {
    font-size: 11.5px;
    color: ${P.textMute};
    font-weight: 300;
    letter-spacing: 0.02em;
  }

  .status-pill {
    display: inline-flex; align-items: center; gap: 6px;
    padding: 5px 10px;
    background: ${HEALTH_GREEN_SOFT};
    border-radius: 999px;
    font-size: 11px;
    font-weight: 500;
    color: ${HEALTH_GREEN};
    letter-spacing: 0.02em;
    margin-bottom: 16px;
  }
  .status-pill .dot {
    width: 7px; height: 7px; border-radius: 50%;
    background: ${HEALTH_GREEN};
  }
  .status-pill.warn {
    background: rgba(203, 138, 72, 0.12);
    color: #CB8A48;
  }
  .status-pill.warn .dot { background: #CB8A48; }
  .status-pill.muted {
    background: ${P.surfaceAlt};
    color: ${P.textMute};
  }
  .status-pill.muted .dot { background: ${P.textMute}; }

  .meta-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 10px 18px;
    padding: 14px 0;
    border-top: 1px solid ${P.border};
    border-bottom: 1px solid ${P.border};
    margin-bottom: 16px;
  }
  .meta-label {
    font-size: 9.5px;
    color: ${P.textMute};
    letter-spacing: 0.12em;
    text-transform: uppercase;
    font-weight: 500;
    margin-bottom: 3px;
  }
  .meta-value {
    font-size: 13px;
    color: ${P.text};
    font-weight: 400;
  }
  .meta-value.muted {
    color: ${P.textMute};
    font-weight: 300;
  }

  .btn-row {
    display: flex; gap: 10px;
  }
  .btn-primary {
    flex: 1;
    padding: 12px 16px;
    background: ${P.primary};
    color: #F2EDE3;
    border: none;
    border-radius: 12px;
    cursor: pointer;
    font-family: 'DM Sans', sans-serif;
    font-size: 12.5px; font-weight: 500;
    letter-spacing: 0.04em;
    transition: opacity 0.15s;
  }
  .btn-primary:hover { opacity: 0.9; }
  .btn-primary:disabled { opacity: 0.5; cursor: default; }

  .btn-ghost {
    flex: 1;
    padding: 12px 16px;
    background: none;
    border: 1.5px solid ${P.border};
    border-radius: 12px;
    cursor: pointer;
    font-family: 'DM Sans', sans-serif;
    font-size: 12.5px; font-weight: 500;
    color: ${P.textMid};
    letter-spacing: 0.04em;
    transition: all 0.15s;
  }
  .btn-ghost:hover { border-color: ${P.primaryMid}; color: ${P.primary}; }

  .footer-note {
    font-size: 11.5px;
    color: ${P.textMute};
    font-weight: 300;
    line-height: 1.6;
    text-align: center;
    margin-top: 24px;
    padding: 0 20px;
  }
`;

function formatRelative(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const diffMs = Date.now() - d.getTime();
  const diffMin = Math.round(diffMs / 60000);
  if (diffMin < 1) return "Just now";
  if (diffMin < 60) return `${diffMin} min ago`;
  const diffHr = Math.round(diffMin / 60);
  if (diffHr < 24) return `${diffHr} hr ago`;
  const diffDay = Math.round(diffHr / 24);
  if (diffDay < 7) return `${diffDay}d ago`;
  return d.toLocaleDateString();
}

function formatDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

// Whether to offer "Connect Fitbit" to users who have never connected.
// Off: the Google consent screen is in Testing mode (see
// app/core/config.py), so only allow-listed accounts could finish the
// flow. Until that changes, the card serves users who already connected:
// a Connected status, or the Reconnect that Profile links to.
export const OFFER_FITBIT_CONNECT_TO_EVERYONE = false;

export function showFitbitCard(fitbit, offerConnect = OFFER_FITBIT_CONNECT_TO_EVERYONE) {
  if (!fitbit) return false;
  return Boolean(fitbit.connected || fitbit.needs_reconnect || offerConnect);
}

export default function Wearables() {
  const navigate = useNavigate();
  const location = useLocation();
  const { token } = useAuth();

  const [ouraStatus, setOuraStatus] = useState(null);
  const [ouraToday, setOuraToday] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  // Fitbit's row from /wearables/status, or null when the server does
  // not list Fitbit. Server truth only; there is no local mirror.
  const [fitbit, setFitbit] = useState(null);
  const [fitbitConnecting, setFitbitConnecting] = useState(false);
  const [fitbitError, setFitbitError] = useState("");
  const fitbitCardRef = useRef(null);
  const [lastSyncedAt, setLastSyncedAt] = useState(null);


  // ── Apple Health (iOS native shell only) ──
  // Local flags are the optimistic mirror; GET /wearables/status is the
  // authority and overrides them once it lands. Neither one is set by
  // merely completing the iOS permission sheet any more — that resolves
  // identically for Allow and Don't Allow, which is how Apple Health
  // used to report "Connected" without ever having been connected.
  const [healthKitConnected, setHealthKitConnected] = useState(isHealthKitConnected());
  const [healthKitPending, setHealthKitPending] = useState(isHealthKitPending());
  const [healthKitLastSync, setHealthKitLastSync] = useState(healthKitLastSyncedAt());
  const [healthKitConnecting, setHealthKitConnecting] = useState(false);
  const [healthKitSyncing, setHealthKitSyncing] = useState(false);
  const [healthKitError, setHealthKitError] = useState("");

  const handleHealthKitConnect = async () => {
    if (!token || healthKitConnecting) return;
    setHealthKitError("");
    setHealthKitConnecting(true);
    try {
      await connectHealthKit(token);
      // Do NOT assume success — the first sync runs in the background
      // and only data landing promotes this to connected.
      setHealthKitConnected(isHealthKitConnected());
      setHealthKitPending(isHealthKitPending());
      setHealthKitLastSync(healthKitLastSyncedAt());
    } catch (err) {
      setHealthKitError(err?.message || "Couldn't connect Apple Health. Please try again.");
    } finally {
      setHealthKitConnecting(false);
    }
  };

  const handleHealthKitSync = async () => {
    if (!token || healthKitSyncing) return;
    setHealthKitSyncing(true);
    try {
      await syncHealthKitToday(token, { force: true });
      setHealthKitConnected(isHealthKitConnected());
      setHealthKitPending(isHealthKitPending());
      setHealthKitLastSync(healthKitLastSyncedAt());
    } finally {
      setHealthKitSyncing(false);
    }
  };

  useEffect(() => {
    if (!token) return;
    let cancelled = false;

    // Returning from the Oura OAuth round-trip lands back on this same
    // mounted Wearables instance (DeepLinkHandler routes to
    // /wearables?oura=connected|error) rather than a fresh one, so
    // nothing else clears the "Connecting…" button or re-pulls status.
    // Watching location.search — not just token — makes that return
    // trip refresh the screen the same way a fresh mount would.
    if (new URLSearchParams(location.search).has("oura")) {
      setConnecting(false);
      // Drop the query param so it isn't reprocessed and doesn't linger
      // in the URL indefinitely.
      navigate("/wearables", { replace: true });
    }

    wearablesApi.status(token)
      .then((res) => {
        if (cancelled || !res?.providers) return;
        setFitbit(res.providers.find((p) => p.key === "fitbit") || null);
        const hk = res.providers.find((p) => p.key === "apple_health");
        if (!hk) return;
        // Server truth wins in both directions: it clears a stale local
        // "connected" and confirms one the device hasn't observed yet.
        setHealthKitConnected(hk.connected);
        if (hk.connected) setHealthKitPending(false);
      })
      .catch(() => { /* offline — keep the local mirror */ });

    Promise.all([
      ouraApi.status(token).catch(() => null),
      ouraApi.today(token).catch(() => null),
    ]).then(([status, today]) => {
      if (cancelled) return;
      setOuraStatus(status);
      setOuraToday(today);
      // ``/oura/today`` doesn't expose a created_at — ``last synced``
      // is approximated by the fetch completing when synced=true.
      // If we wanted a server-stamped value we'd pull the latest
      // ``wearable_daily`` row; this is close enough for UX.
      if (today?.synced) setLastSyncedAt(new Date().toISOString());
      setLoading(false);
    });
    return () => { cancelled = true; };
  }, [token, location.search]);

  // Profile's Fitbit row links to /wearables#fitbit: bring the card
  // into view once it has rendered.
  useEffect(() => {
    if (location.hash === "#fitbit" && fitbit && fitbitCardRef.current?.scrollIntoView) {
      fitbitCardRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [location.hash, fitbit]);

  // Same hand-off as Oura: the backend returns an authorize URL and the
  // browser goes there. The callback lands on /profile?fitbit=...
  const handleFitbitConnect = () => {
    if (!token || fitbitConnecting) return;
    setFitbitConnecting(true);
    setFitbitError("");
    fitbitApi.authorize(token)
      .then((res) => { window.location.href = res.authorize_url; })
      .catch(() => {
        setFitbitConnecting(false);
        setFitbitError("The Fitbit connection couldn't start.");
      });
  };

  // Oura entry point gate (utils/features.js). Read per render.
  const ouraUI = isOuraUIEnabled();
  const isConnected = !!ouraStatus?.connected;
  const expiresAt = ouraStatus?.expires_at ? new Date(ouraStatus.expires_at) : null;
  const isExpired = expiresAt && expiresAt.getTime() < Date.now();

  const handleSync = async () => {
    if (!token || syncing) return;
    setSyncing(true);
    try {
      await ouraApi.sync(token);
      setLastSyncedAt(new Date().toISOString());
      // Refresh today to reflect any newly pulled metrics.
      const today = await ouraApi.today(token).catch(() => null);
      setOuraToday(today);
    } catch {
      // Silent — a sync failure is annoying but recoverable. The
      // disconnected / reconnect path covers the token-expired case
      // separately.
    } finally {
      setSyncing(false);
    }
  };

  const handleDisconnect = async () => {
    if (!token || disconnecting) return;
    setDisconnecting(true);
    try {
      setOuraStatus(await ouraApi.disconnect(token));
      setOuraToday(null);
    } catch {
      // Leave the card as it was; the user can retry.
    } finally {
      setDisconnecting(false);
    }
  };

  const handleConnect = () => {
    if (!token || connecting) return;
    setConnecting(true);
    // In the native shell, tag the flow so the backend's post-exchange
    // redirect returns via haiven:// (DeepLinkHandler) instead of
    // leaving the user in Safari on the web app.
    ouraApi.authorize(token, isNativeIOS() ? 'ios' : undefined)
      .then(res => { window.location.href = res.authorize_url; })
      .catch(() => setConnecting(false));
  };

  let pillClass = "muted";
  let pillText = "Not connected";
  if (isConnected && !isExpired) {
    pillClass = "";
    pillText = "Connected";
  } else if (isConnected && isExpired) {
    pillClass = "warn";
    pillText = "Reconnect needed";
  }

  return (
    <>
      <style>{STYLES}</style>
      <div className="wearables-screen">
        {/* Top bar with back */}
        <div className="topbar fu d1">
          <button aria-label="Back" onClick={() => navigate(-1)}>
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none"
              stroke="currentColor" strokeWidth="1.8"
              strokeLinecap="round" strokeLinejoin="round">
              <path d="M15 18l-6-6 6-6" />
            </svg>
          </button>
        </div>

        <div className="fu d1">
          <h1 className="title">Data connections</h1>
          <p className="subtitle">
            Connect your wearables. Haiven brings your sleep, energy, and
            activity signals into your daily score.
          </p>
        </div>

        {/* Oura card. Hidden in the native iOS build while
            VITE_IOS_OURA_ENABLED is off (App Store 1.0, see
            utils/features.js); always shown on web. */}
        {ouraUI && (
        <div className="integration-card fu d2">
          <div className="integration-header">
            <div className="integration-icon">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none"
                stroke={P.primary} strokeWidth="1.6"
                strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10" />
                <path d="M12 6v6l4 2" />
              </svg>
            </div>
            <div>
              <div className="integration-title">Oura</div>
              <div className="integration-sub">
                HRV · Sleep · Readiness · Resting HR
              </div>
            </div>
          </div>

          <div className={`status-pill ${pillClass}`}>
            <span className="dot" />
            {pillText}
          </div>

          {isConnected && (
            <div className="meta-grid">
              <div>
                <div className="meta-label">Connected</div>
                <div className="meta-value">
                  {formatDate(ouraStatus?.connected_at)}
                </div>
              </div>
              <div>
                <div className="meta-label">Last sync</div>
                <div className="meta-value">
                  {loading
                    ? "…"
                    : (formatRelative(lastSyncedAt) || "—")}
                </div>
              </div>
              <div>
                <div className="meta-label">Today's HRV</div>
                <div className={`meta-value${ouraToday?.hrv ? "" : " muted"}`}>
                  {ouraToday?.hrv != null
                    ? `${Math.round(ouraToday.hrv)} ms`
                    : "Not yet today"}
                </div>
              </div>
              <div>
                <div className="meta-label">Readiness</div>
                <div className={`meta-value${ouraToday?.readiness ? "" : " muted"}`}>
                  {/*
                    Three states for the overnight-summary fields
                    (readiness, sleep, resting HR). The backend
                    sets ``not_worn=true`` when daytime HRV came
                    through but no overnight summary published —
                    that's the ring-off-the-wrist case, which we
                    surface as a friendly italic line instead of
                    the "Not yet today" timing-language we use
                    while waiting for Oura to finalize.
                  */}
                  {ouraToday?.readiness != null
                    ? Math.round(ouraToday.readiness)
                    : ouraToday?.not_worn
                      ? <em style={{ fontStyle: "italic", opacity: 0.75 }}>Ring not worn</em>
                      : "Not yet today"}
                </div>
              </div>
            </div>
          )}

          <div className="btn-row">
            {isConnected && !isExpired && (
              <button
                className="btn-primary"
                onClick={handleSync}
                disabled={syncing}
              >
                {syncing ? "Syncing…" : "Sync now"}
              </button>
            )}
            {isConnected && (
              <button
                className="btn-ghost"
                onClick={handleDisconnect}
                disabled={disconnecting}
              >
                {disconnecting ? "Disconnecting…" : "Disconnect"}
              </button>
            )}
            {(!isConnected || isExpired) && (
              <button
                className="btn-primary"
                onClick={handleConnect}
                disabled={connecting}
              >
                {connecting
                  ? "Connecting…"
                  : isExpired
                    ? "Reconnect"
                    : "Connect Oura"}
              </button>
            )}
          </div>
        </div>
        )}

        {/* Apple Health card — iOS native shell only. Connection state
            is client-side (HealthKit never discloses read grants); data
            lands in wearable_daily via /healthkit/sync. */}
        {isHealthKitSupported() && (
          <div className="integration-card fu d2">
            <div className="integration-header">
              <div className="integration-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none"
                  stroke={P.primary} strokeWidth="1.6"
                  strokeLinecap="round" strokeLinejoin="round">
                  <path d="M20.8 4.6a5.5 5.5 0 0 0-7.8 0L12 5.6l-1-1a5.5 5.5 0 0 0-7.8 7.8l1 1L12 21.2l7.8-7.8 1-1a5.5 5.5 0 0 0 0-7.8z" />
                </svg>
              </div>
              <div>
                <div className="integration-title">Apple Health</div>
                <div className="integration-sub">
                  HRV · Sleep · Resting HR · Steps
                </div>
              </div>
            </div>

            <div className={`status-pill ${healthKitConnected ? "" : "muted"}`}>
              <span className="dot" />
              {healthKitConnected
                ? "Connected"
                : healthKitPending
                  ? "Waiting for data"
                  : "Not connected"}
            </div>

            {healthKitConnected && (
              <div className="meta-grid">
                <div>
                  <div className="meta-label">Last sync</div>
                  <div className="meta-value">
                    {formatRelative(healthKitLastSync) || "—"}
                  </div>
                </div>
                <div>
                  <div className="meta-label">Source</div>
                  <div className="meta-value">iPhone / Watch</div>
                </div>
              </div>
            )}

            {healthKitError && (
              <p style={{ fontSize: 12, color: "#C0392B", fontWeight: 300, margin: "0 0 10px" }}>
                {healthKitError}
              </p>
            )}

            <div className="btn-row">
              {healthKitConnected ? (
                <button
                  className="btn-primary"
                  onClick={handleHealthKitSync}
                  disabled={healthKitSyncing}
                >
                  {healthKitSyncing ? "Syncing…" : "Sync now"}
                </button>
              ) : (
                <button
                  className="btn-primary"
                  onClick={handleHealthKitConnect}
                  disabled={healthKitConnecting}
                >
                  {healthKitConnecting ? "Connecting…" : "Connect Apple Health"}
                </button>
              )}
            </div>
          </div>
        )}

        {/* Fitbit card — see showFitbitCard for who sees it. */}
        {showFitbitCard(fitbit) && (
          <div className="integration-card fu d2" id="fitbit" data-testid="fitbit-card" ref={fitbitCardRef}>
            <div className="integration-header">
              <div className="integration-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none"
                  stroke={P.primary} strokeWidth="1.6"
                  strokeLinecap="round" strokeLinejoin="round">
                  <rect x="7" y="6" width="10" height="12" rx="3" />
                  <path d="M9 6V3h6v3M9 18v3h6v-3" />
                </svg>
              </div>
              <div>
                <div className="integration-title">Fitbit</div>
                <div className="integration-sub">HRV · Sleep · Resting HR</div>
              </div>
            </div>

            {fitbit.needs_reconnect ? (
              <div className="status-pill warn"><span className="dot" />Reconnect needed</div>
            ) : fitbit.connected ? (
              <div className="status-pill"><span className="dot" />Connected</div>
            ) : (
              <div className="status-pill muted"><span className="dot" />Not connected</div>
            )}

            {fitbitError && (
              <p style={{ fontSize: 12, color: P.danger, fontWeight: 300, margin: "0 0 10px" }}>
                {fitbitError}
              </p>
            )}

            {!fitbit.connected && (
              <div className="btn-row">
                <button
                  className="btn-primary"
                  onClick={handleFitbitConnect}
                  disabled={fitbitConnecting}
                >
                  {fitbitConnecting
                    ? "Connecting…"
                    : fitbit.needs_reconnect ? "Reconnect Fitbit" : "Connect Fitbit"}
                </button>
              </div>
            )}
          </div>
        )}

        {/* Apple Health pointer card — iPhone/iPad *browser* only.
            HealthKit is unreachable from the web, so instead of hiding
            Apple Health entirely (desktop/Android behavior) we surface
            it as a "get the app" pointer. Mutually exclusive with the
            native card above: isIOSBrowser() is false inside any
            Capacitor shell. */}
        {!isHealthKitSupported() && isIOSBrowser() && (
          <div className="integration-card fu d2">
            <div className="integration-header">
              <div className="integration-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none"
                  stroke={P.primary} strokeWidth="1.6"
                  strokeLinecap="round" strokeLinejoin="round">
                  <path d="M20.8 4.6a5.5 5.5 0 0 0-7.8 0L12 5.6l-1-1a5.5 5.5 0 0 0-7.8 7.8l1 1L12 21.2l7.8-7.8 1-1a5.5 5.5 0 0 0 0-7.8z" />
                </svg>
              </div>
              <div>
                <div className="integration-title">Apple Health</div>
                <div className="integration-sub">
                  HRV · Sleep · Resting HR · Steps
                </div>
              </div>
            </div>

            <div className="status-pill muted">
              <span className="dot" />
              Available in the iOS app
            </div>

            <p style={{ fontSize: 12.5, color: P.textMid, fontWeight: 300, lineHeight: 1.6, margin: "0 0 12px" }}>
              Apple Watch and Apple Health sync through the Haiven iOS app.
            </p>

            <div className="btn-row">
              <button
                className="btn-primary"
                onClick={() => navigate("/get-the-app?from=wearables")}
              >
                Get the app
              </button>
            </div>
          </div>
        )}

        <p className="footer-note fu d3">
          {/* Web-iOS gets the short variant too — the pointer card above
              already carries the "available in the iOS app" message. */}
          {isHealthKitSupported() || isIOSBrowser()
            ? "More integrations coming soon — Garmin and others. "
            : "Apple Health is available in the Haiven iOS app. More integrations coming soon — Garmin and others. "}
          Ping <a href="mailto:hello@haiven.co"
            style={{ color: P.primary, textDecoration: "none" }}>hello@haiven.co
          </a> if you'd like priority on one.
        </p>
      </div>
    </>
  );
}
